import type { GameServer } from '@agent8-dev/gameserver';
import { createV8Session } from '@rb/v8-client/session';
import type { V8Session } from '@rb/v8-client';
import { resolveVerseEntry } from '@rb/shared/admin-verses';

const TAG = '[VerseConnectionManager]';

export interface VerseConnection {
  verseId: string;
  server: GameServer;
}

export type GetPrivyToken = () => Promise<string | null>;

/**
 * Owns the single active V8 GameServer connection for the admin app.
 * Responsibilities:
 *  - Switch to a verse: tear down existing connection, open new via v8-client/session
 *  - v8-client 가 토큰 refresh + 재연결 자동 처리 (verse 별 라이프사이클은 manager 가 관리)
 *
 * Single active verse only (v1). Multi-verse simultaneous connections deferred to v2.
 */
export class VerseConnectionManager {
  private active: { verseId: string; session: V8Session } | null = null;

  constructor(
    private readonly account: string,
    private readonly getPrivyToken: GetPrivyToken
  ) {}

  get current(): VerseConnection | null {
    if (!this.active) return null;
    return { verseId: this.active.verseId, server: this.active.session.server };
  }

  /**
   * Switch to a new verse. Tears down any existing session, opens a new one.
   */
  async switchTo(verseAliasOrId: string, gatewayUrl: string): Promise<VerseConnection> {
    const verse = resolveVerseEntry(verseAliasOrId);

    await this.disconnect();

    const session = await createV8Session({
      config: {
        account: this.account,
        verse: verse.verseId,
        env: verse.requiresAuth ? 'prod' : 'preview',
        // per-verse override(현재 미사용 escape hatch)가 있으면 우선, 없으면 토글 gateway.
        remoteUrl: verse.remoteUrl ?? gatewayUrl,
      },
      bearerProvider: verse.requiresAuth
        ? async () => {
            const token = await this.getPrivyToken();
            if (!token) throw new Error(`${TAG} no Privy access token available`);
            return token;
          }
        : undefined,
    });

    this.active = { verseId: verse.verseId, session };
    console.info(`${TAG} switched to ${verse.alias} (${verse.verseId})`); // keep
    return { verseId: verse.verseId, server: session.server };
  }

  /** Tear down the active session and clear refresh/reconnect timers. */
  async disconnect(): Promise<void> {
    if (this.active) {
      try {
        await this.active.session.shutdown();
      } catch (err) {
        console.warn(`${TAG} shutdown error (ignored):`, err); // keep
      }
      this.active = null;
    }
  }
}
